import { useEffect, useMemo } from "react";
import {
  BufferGeometry,
  Float32BufferAttribute,
  LineBasicMaterial,
  type Vector3,
} from "three";

/** Soft accent color for an idle guide line (matches the constraint outline). */
const GUIDE_COLOR_IDLE = 0x4aa3ff;
/** Bright color for a guide line the part is currently snapped to. */
const GUIDE_COLOR_SNAPPED = 0x00ff88;

const HELPER_USER_DATA = { userData: { isDragHelper: true } };

/** One center guide line spanning the bounds through the center along `dir`. */
export interface GuideAxis {
  /** Unit direction of the line in world space. */
  dir: Vector3;
  /** Half the bounds extent along `dir` (line reaches center ± halfLength). */
  halfLength: number;
  /** Whether the dragged part is currently snapped to this center line. */
  snapped: boolean;
  /** Stable key for React. */
  key: string;
}

/** A free guide segment between two explicit endpoints (e.g. a 45° diagonal). */
export interface GuideSegment {
  /** Segment start in world space. */
  start: Vector3;
  /** Segment end in world space. */
  end: Vector3;
  /** Whether the dragged part is currently snapped to this line. */
  snapped: boolean;
  /** Stable key for React. */
  key: string;
}

const guideMaterial = (snapped: boolean) =>
  new LineBasicMaterial({
    color: snapped ? GUIDE_COLOR_SNAPPED : GUIDE_COLOR_IDLE,
    opacity: snapped ? 1 : 0.45,
    transparent: true,
    depthTest: false,
  });

const SegmentLine = ({ start, end, snapped }: Omit<GuideSegment, "key">) => {
  const geometry = useMemo(() => {
    const geo = new BufferGeometry();
    geo.setAttribute(
      "position",
      new Float32BufferAttribute(
        [start.x, start.y, start.z, end.x, end.y, end.z],
        3,
      ),
    );
    return geo;
  }, [start, end]);

  const material = useMemo(() => guideMaterial(snapped), [snapped]);

  useEffect(() => {
    return () => {
      geometry.dispose();
      material.dispose();
    };
  }, [geometry, material]);

  return (
    <lineSegments
      geometry={geometry}
      material={material}
      renderOrder={1000}
      {...HELPER_USER_DATA}
    />
  );
};

const GuideLine = ({ center, axis }: { center: Vector3; axis: GuideAxis }) => {
  const geometry = useMemo(() => {
    const start = center.clone().addScaledVector(axis.dir, -axis.halfLength);
    const end = center.clone().addScaledVector(axis.dir, axis.halfLength);
    const geo = new BufferGeometry();
    geo.setAttribute(
      "position",
      new Float32BufferAttribute(
        [start.x, start.y, start.z, end.x, end.y, end.z],
        3,
      ),
    );
    return geo;
    // center/axis are value-stable per drag frame; recompute when they change.
  }, [center, axis.dir, axis.halfLength]);

  const material = useMemo(() => guideMaterial(axis.snapped), [axis.snapped]);

  useEffect(() => {
    return () => {
      geometry.dispose();
      material.dispose();
    };
  }, [geometry, material]);

  return (
    <lineSegments
      geometry={geometry}
      material={material}
      renderOrder={1000}
      {...HELPER_USER_DATA}
    />
  );
};

/**
 * Renders the center guide lines (e.g. horizontal / vertical middle of a
 * surface) that a draggable part can snap to. Each line spans the movement
 * bounds and is highlighted while the part is snapped to it.
 */
export const DragGuideLines = ({
  center,
  axes,
  segments = [],
}: {
  center: Vector3;
  axes: GuideAxis[];
  segments?: GuideSegment[];
}) => {
  return (
    <group {...HELPER_USER_DATA}>
      {axes.map((axis) => (
        <GuideLine key={axis.key} center={center} axis={axis} />
      ))}
      {segments.map(({ key, ...segment }) => (
        <SegmentLine key={key} {...segment} />
      ))}
    </group>
  );
};

export default DragGuideLines;
